/**
 * FREE GLIDE LEAGUE — GAFOR-Regionen (83 Gebiete) + FGL-Regionen (20 Gruppen)
 *
 * GAFOR = General Aviation Forecast — 83 Vorhersagegebiete des DWD
 * Jede Region hat eine ID (1-83), eine Referenzstation mit Koordinaten
 * und einen Gelaendetyp fuer die Modellflug-Indexberechnung.
 *
 * Quellen:
 * - DWD Segelflug-Gebietsvorhersagen
 * - ICAO-Karte Deutschland
 *
 * Lizenz: GeoNutzV — Quelle: Deutscher Wetterdienst
 */

export interface GaforRegion {
  id: number
  name: string
  center: { lat: number; lon: number }
  elevation: number          // Gebiets-Grundhoehe in Metern
  referenceStation: string
  stationCoords: { lat: number; lon: number }
  terrainType: 'coast' | 'flatland' | 'hills' | 'mountains' | 'alps'
  polygon?: [number, number][]  // [lat, lon] Eckpunkte der Regionsgrenze
}

export interface FglRegion {
  id: string
  name: string
  gaforRegions: number[]
  terrainType: 'coast' | 'flatland' | 'hills' | 'mountains' | 'alps'
  center: { lat: number; lon: number }
}

// ═══════════════════════════════════════════
// 83 GAFOR-Regionen mit Koordinaten
// ═══════════════════════════════════════════

export const GAFOR_REGIONS: GaforRegion[] = [
  // --- 01-10: Nordwest-Deutschland (Kueste + Flachland) ---
  { id: 1,  name: 'Ostfriesland',            center: { lat: 53.58, lon: 7.78 },  elevation: 5,   referenceStation: 'Wittmund',        stationCoords: { lat: 53.55, lon: 7.67 },  terrainType: 'coast' },
  { id: 2,  name: 'Oldenburger Land',        center: { lat: 53.15, lon: 8.22 },  elevation: 15,  referenceStation: 'Oldenburg',       stationCoords: { lat: 53.14, lon: 8.21 },  terrainType: 'flatland' },
  { id: 3,  name: 'Emsland',                 center: { lat: 52.75, lon: 7.40 },  elevation: 30,  referenceStation: 'Lingen',          stationCoords: { lat: 52.52, lon: 7.31 },  terrainType: 'flatland' },
  { id: 4,  name: 'Ostholstein / Luebecker Bucht', center: { lat: 54.10, lon: 10.70 }, elevation: 10, referenceStation: 'Luebeck',   stationCoords: { lat: 53.88, lon: 10.69 }, terrainType: 'coast' },
  { id: 5,  name: 'Schleswig / Flensburg',   center: { lat: 54.55, lon: 9.45 },  elevation: 20,  referenceStation: 'Schleswig',       stationCoords: { lat: 54.53, lon: 9.55 },  terrainType: 'coast' },
  { id: 6,  name: 'Dithmarschen / Nordfriesland', center: { lat: 54.30, lon: 9.05 }, elevation: 5, referenceStation: 'St. Peter-Ording', stationCoords: { lat: 54.33, lon: 8.60 }, terrainType: 'coast' },
  { id: 7,  name: 'Hamburg / Unterelbe',      center: { lat: 53.55, lon: 9.98 },  elevation: 10,  referenceStation: 'Hamburg-Fuhlsbuettel', stationCoords: { lat: 53.63, lon: 9.99 }, terrainType: 'flatland' },
  { id: 8,  name: 'Lueneburger Heide',       center: { lat: 53.10, lon: 10.05 }, elevation: 50,  referenceStation: 'Lueneburg',       stationCoords: { lat: 53.25, lon: 10.41 }, terrainType: 'flatland' },
  { id: 9,  name: 'Weser-Ems / Osnabrueck',  center: { lat: 52.45, lon: 8.05 },  elevation: 50,  referenceStation: 'Osnabrueck',      stationCoords: { lat: 52.25, lon: 8.05 },  terrainType: 'flatland' },
  { id: 10, name: 'Bremen / Wesermarsch',    center: { lat: 53.10, lon: 8.80 },  elevation: 5,   referenceStation: 'Bremen',          stationCoords: { lat: 53.05, lon: 8.80 },  terrainType: 'flatland' },

  // --- 11-20: Nordost-Deutschland (Flachland) ---
  { id: 11, name: 'Mecklenburg-West',        center: { lat: 53.85, lon: 11.45 }, elevation: 20,  referenceStation: 'Schwerin',        stationCoords: { lat: 53.64, lon: 11.39 }, terrainType: 'flatland' },
  { id: 12, name: 'Mecklenburg-Ost / Ruegen', center: { lat: 54.15, lon: 12.80 }, elevation: 10, referenceStation: 'Rostock-Warnemuende', stationCoords: { lat: 54.18, lon: 12.08 }, terrainType: 'coast' },
  { id: 13, name: 'Vorpommern',              center: { lat: 53.90, lon: 13.40 }, elevation: 15,  referenceStation: 'Greifswald',      stationCoords: { lat: 54.10, lon: 13.40 }, terrainType: 'coast' },
  { id: 14, name: 'Uckermark / Prenzlau',    center: { lat: 53.35, lon: 13.85 }, elevation: 50,  referenceStation: 'Angerm\u00fcnde', stationCoords: { lat: 53.03, lon: 14.00 }, terrainType: 'flatland' },
  { id: 15, name: 'Prignitz / Ruppin',       center: { lat: 52.95, lon: 12.40 }, elevation: 40,  referenceStation: 'Neuruppin',       stationCoords: { lat: 52.90, lon: 12.80 }, terrainType: 'flatland' },
  { id: 16, name: 'Altmark / Stendal',       center: { lat: 52.60, lon: 11.86 }, elevation: 40,  referenceStation: 'Stendal',         stationCoords: { lat: 52.59, lon: 11.85 }, terrainType: 'flatland' },
  { id: 17, name: 'Havelland',               center: { lat: 52.40, lon: 13.05 }, elevation: 35,  referenceStation: 'Potsdam',         stationCoords: { lat: 52.38, lon: 13.06 }, terrainType: 'flatland' },
  { id: 18, name: 'Niederlausitz / Spreewald', center: { lat: 51.85, lon: 13.75 }, elevation: 65, referenceStation: 'Cottbus',        stationCoords: { lat: 51.78, lon: 14.32 }, terrainType: 'flatland' },
  { id: 19, name: 'Berliner Umland / Oder',  center: { lat: 52.50, lon: 14.10 }, elevation: 40,  referenceStation: 'Frankfurt (Oder)', stationCoords: { lat: 52.35, lon: 14.55 }, terrainType: 'flatland' },
  { id: 20, name: 'Flaemig / Wittenberg',    center: { lat: 51.90, lon: 12.65 }, elevation: 90,  referenceStation: 'Wittenberg',      stationCoords: { lat: 51.87, lon: 12.65 }, terrainType: 'flatland' },

  // --- 21-30: Zentral-West Deutschland (Mittelgebirge) ---
  { id: 21, name: 'Muensterland',            center: { lat: 52.00, lon: 7.60 },  elevation: 60,  referenceStation: 'Muenster',        stationCoords: { lat: 51.97, lon: 7.63 },  terrainType: 'flatland' },
  { id: 22, name: 'Teutoburger Wald / Lippe', center: { lat: 51.90, lon: 8.65 }, elevation: 200, referenceStation: 'Bielefeld',       stationCoords: { lat: 52.02, lon: 8.53 },  terrainType: 'hills' },
  { id: 23, name: 'Weserbergland',           center: { lat: 52.00, lon: 9.40 },  elevation: 200, referenceStation: 'Hameln',          stationCoords: { lat: 52.10, lon: 9.36 },  terrainType: 'hills' },
  { id: 24, name: 'Harz-West / Solling',     center: { lat: 51.80, lon: 9.90 },  elevation: 300, referenceStation: 'Goettingen',      stationCoords: { lat: 51.53, lon: 9.95 },  terrainType: 'hills' },
  { id: 25, name: 'Harz',                    center: { lat: 51.80, lon: 10.75 }, elevation: 500, referenceStation: 'Brocken',         stationCoords: { lat: 51.80, lon: 10.62 }, terrainType: 'mountains' },
  { id: 26, name: 'Saale-Unstrut / Halle',   center: { lat: 51.35, lon: 11.80 }, elevation: 120, referenceStation: 'Halle',           stationCoords: { lat: 51.50, lon: 11.97 }, terrainType: 'flatland' },
  { id: 27, name: 'Thueringer Becken',       center: { lat: 51.00, lon: 11.00 }, elevation: 200, referenceStation: 'Erfurt',          stationCoords: { lat: 50.98, lon: 10.96 }, terrainType: 'hills' },
  { id: 28, name: 'Thueringer Wald',         center: { lat: 50.65, lon: 10.80 }, elevation: 500, referenceStation: 'Schmücke',        stationCoords: { lat: 50.65, lon: 10.77 }, terrainType: 'mountains' },
  { id: 29, name: 'Hannover / Hildesheim',   center: { lat: 52.35, lon: 9.75 },  elevation: 60,  referenceStation: 'Hannover',        stationCoords: { lat: 52.46, lon: 9.68 },  terrainType: 'flatland' },
  { id: 30, name: 'Braunschweig / Wolfsburg', center: { lat: 52.30, lon: 10.55 }, elevation: 80, referenceStation: 'Braunschweig',    stationCoords: { lat: 52.29, lon: 10.45 }, terrainType: 'flatland' },

  // --- 31-40: Zentral-Deutschland ---
  { id: 31, name: 'Saechsisches Tiefland / Leipzig', center: { lat: 51.35, lon: 12.40 }, elevation: 120, referenceStation: 'Leipzig', stationCoords: { lat: 51.32, lon: 12.42 }, terrainType: 'flatland' },
  { id: 32, name: 'Nordsachsen / Torgau',    center: { lat: 51.55, lon: 13.00 }, elevation: 100, referenceStation: 'Torgau',          stationCoords: { lat: 51.56, lon: 12.99 }, terrainType: 'flatland' },
  { id: 33, name: 'Dresden / Elbtal',        center: { lat: 51.05, lon: 13.75 }, elevation: 120, referenceStation: 'Dresden',         stationCoords: { lat: 51.13, lon: 13.77 }, terrainType: 'flatland' },
  { id: 34, name: 'Erzgebirge-Nord',         center: { lat: 50.80, lon: 13.20 }, elevation: 400, referenceStation: 'Chemnitz',        stationCoords: { lat: 50.84, lon: 12.87 }, terrainType: 'hills' },
  { id: 35, name: 'Erzgebirge-Sued / Fichtelberg', center: { lat: 50.50, lon: 12.95 }, elevation: 700, referenceStation: 'Fichtelberg', stationCoords: { lat: 50.43, lon: 12.95 }, terrainType: 'mountains' },
  { id: 36, name: 'Oberlausitz / Bautzen',   center: { lat: 51.10, lon: 14.45 }, elevation: 200, referenceStation: 'Bautzen',         stationCoords: { lat: 51.18, lon: 14.44 }, terrainType: 'hills' },
  { id: 37, name: 'Saechsische Schweiz / Zittauer Gebirge', center: { lat: 50.85, lon: 14.20 }, elevation: 350, referenceStation: 'Zinnwald', stationCoords: { lat: 50.73, lon: 13.75 }, terrainType: 'hills' },
  { id: 38, name: 'Vogtland / Plauen',       center: { lat: 50.50, lon: 12.15 }, elevation: 400, referenceStation: 'Plauen',          stationCoords: { lat: 50.49, lon: 12.13 }, terrainType: 'hills' },
  { id: 39, name: 'Oberfranken / Fichtelgebirge', center: { lat: 50.20, lon: 11.80 }, elevation: 450, referenceStation: 'Hof',        stationCoords: { lat: 50.31, lon: 11.88 }, terrainType: 'hills' },
  { id: 40, name: 'Magdeburg / Boerde',      center: { lat: 52.10, lon: 11.60 }, elevation: 60,  referenceStation: 'Magdeburg',       stationCoords: { lat: 52.13, lon: 11.58 }, terrainType: 'flatland' },

  // --- 41-50: West-Zentral (Rhein-Gebiet) ---
  { id: 41, name: 'Niederrhein / Duesseldorf', center: { lat: 51.30, lon: 6.80 }, elevation: 40, referenceStation: 'Duesseldorf',     stationCoords: { lat: 51.29, lon: 6.77 },  terrainType: 'flatland' },
  { id: 42, name: 'Ruhrgebiet / Sauerland-Nord', center: { lat: 51.40, lon: 7.50 }, elevation: 100, referenceStation: 'Dortmund',     stationCoords: { lat: 51.52, lon: 7.46 },  terrainType: 'flatland' },
  { id: 43, name: 'Sauerland',               center: { lat: 51.15, lon: 8.10 },  elevation: 400, referenceStation: 'Kahler Asten',    stationCoords: { lat: 51.18, lon: 8.49 },  terrainType: 'hills' },
  { id: 44, name: 'Siegerland / Westerwald', center: { lat: 50.90, lon: 8.00 },  elevation: 350, referenceStation: 'Siegen',          stationCoords: { lat: 50.88, lon: 8.03 },  terrainType: 'hills' },
  { id: 45, name: 'Rhein-Main / Wetterau',   center: { lat: 50.10, lon: 8.77 },  elevation: 110, referenceStation: 'Offenbach',       stationCoords: { lat: 50.10, lon: 8.75 },  terrainType: 'hills' },
  { id: 46, name: 'Eifel',                   center: { lat: 50.35, lon: 6.80 },  elevation: 450, referenceStation: 'Nuerburgring',    stationCoords: { lat: 50.35, lon: 6.94 },  terrainType: 'hills' },
  { id: 47, name: 'Mosel / Hunsrueck',       center: { lat: 49.95, lon: 7.30 },  elevation: 400, referenceStation: 'Hahn',            stationCoords: { lat: 49.95, lon: 7.26 },  terrainType: 'hills' },
  { id: 48, name: 'Taunus / Lahn',           center: { lat: 50.35, lon: 8.20 },  elevation: 350, referenceStation: 'Limburg',         stationCoords: { lat: 50.40, lon: 8.07 },  terrainType: 'hills' },
  { id: 49, name: 'Koeln / Bonn',            center: { lat: 50.80, lon: 7.00 },  elevation: 60,  referenceStation: 'Koeln-Bonn',      stationCoords: { lat: 50.87, lon: 7.16 },  terrainType: 'flatland' },
  { id: 50, name: 'Aachen / Niederrhein-Sued', center: { lat: 50.75, lon: 6.25 }, elevation: 150, referenceStation: 'Aachen',         stationCoords: { lat: 50.78, lon: 6.10 },  terrainType: 'hills' },

  // --- 51-60: Zentral-Sued (Mittelgebirge + Franken) ---
  { id: 51, name: 'Nordhessen / Kassel',     center: { lat: 51.30, lon: 9.50 },  elevation: 200, referenceStation: 'Kassel',          stationCoords: { lat: 51.30, lon: 9.45 },  terrainType: 'hills' },
  { id: 52, name: 'Rhoen',                   center: { lat: 50.50, lon: 10.00 }, elevation: 500, referenceStation: 'Wasserkuppe',     stationCoords: { lat: 50.50, lon: 9.94 },  terrainType: 'mountains' },
  { id: 53, name: 'Spessart / Unterfranken', center: { lat: 49.95, lon: 9.60 },  elevation: 300, referenceStation: 'Wuerzburg',       stationCoords: { lat: 49.77, lon: 9.96 },  terrainType: 'hills' },
  { id: 54, name: 'Mittelfranken / Nuernberg', center: { lat: 49.45, lon: 11.10 }, elevation: 310, referenceStation: 'Nuernberg',     stationCoords: { lat: 49.50, lon: 11.05 }, terrainType: 'hills' },
  { id: 55, name: 'Oberpfalz / Bayerischer Wald-Nord', center: { lat: 49.50, lon: 12.10 }, elevation: 400, referenceStation: 'Weiden', stationCoords: { lat: 49.68, lon: 12.18 }, terrainType: 'hills' },
  { id: 56, name: 'Bayerischer Wald-Sued',   center: { lat: 49.00, lon: 13.00 }, elevation: 600, referenceStation: 'Straubing',       stationCoords: { lat: 48.83, lon: 12.57 }, terrainType: 'mountains' },
  { id: 57, name: 'Pfalz / Nahe',            center: { lat: 49.55, lon: 7.80 },  elevation: 250, referenceStation: 'Bad Kreuznach',   stationCoords: { lat: 49.84, lon: 7.87 },  terrainType: 'hills' },
  { id: 58, name: 'Saarland',                center: { lat: 49.35, lon: 6.95 },  elevation: 300, referenceStation: 'Saarbruecken',    stationCoords: { lat: 49.21, lon: 7.11 },  terrainType: 'hills' },
  { id: 59, name: 'Rheinhessen / Mainz',     center: { lat: 49.85, lon: 8.25 },  elevation: 150, referenceStation: 'Mainz',           stationCoords: { lat: 50.00, lon: 8.26 },  terrainType: 'hills' },
  { id: 60, name: 'Odenwald / Bergstrasse',  center: { lat: 49.65, lon: 8.90 },  elevation: 350, referenceStation: 'Michelstadt',     stationCoords: { lat: 49.68, lon: 9.00 },  terrainType: 'hills' },

  // --- 61-70: Suedwest (Schwarzwald, Oberrhein) ---
  { id: 61, name: 'Oberrhein-Nord / Karlsruhe', center: { lat: 49.00, lon: 8.40 }, elevation: 120, referenceStation: 'Karlsruhe',     stationCoords: { lat: 49.04, lon: 8.36 },  terrainType: 'flatland' },
  { id: 62, name: 'Nordschwarzwald',         center: { lat: 48.60, lon: 8.40 },  elevation: 600, referenceStation: 'Freudenstadt',    stationCoords: { lat: 48.45, lon: 8.41 },  terrainType: 'mountains' },
  { id: 63, name: 'Suedschwarzwald',         center: { lat: 47.90, lon: 8.10 },  elevation: 700, referenceStation: 'Feldberg',        stationCoords: { lat: 47.87, lon: 8.00 },  terrainType: 'mountains' },
  { id: 64, name: 'Oberrhein-Sued / Freiburg', center: { lat: 48.00, lon: 7.85 }, elevation: 200, referenceStation: 'Freiburg',       stationCoords: { lat: 48.02, lon: 7.83 },  terrainType: 'hills' },
  { id: 65, name: 'Hochrhein / Bodensee-West', center: { lat: 47.70, lon: 8.60 }, elevation: 400, referenceStation: 'Singen',         stationCoords: { lat: 47.76, lon: 8.84 },  terrainType: 'hills' },
  { id: 66, name: 'Schwäbische Alb-West',    center: { lat: 48.40, lon: 9.20 },  elevation: 650, referenceStation: 'Muehlacker',      stationCoords: { lat: 48.95, lon: 8.84 },  terrainType: 'mountains' },
  { id: 67, name: 'Schwäbische Alb-Ost',     center: { lat: 48.50, lon: 9.75 },  elevation: 650, referenceStation: 'Ulm',             stationCoords: { lat: 48.40, lon: 9.98 },  terrainType: 'mountains' },
  { id: 68, name: 'Stuttgart / Neckar',      center: { lat: 48.78, lon: 9.18 },  elevation: 250, referenceStation: 'Stuttgart',       stationCoords: { lat: 48.83, lon: 9.20 },  terrainType: 'hills' },
  { id: 69, name: 'Hohenlohe / Frankenhoehe', center: { lat: 49.20, lon: 9.90 }, elevation: 350, referenceStation: 'Oehringen',       stationCoords: { lat: 49.21, lon: 9.51 },  terrainType: 'hills' },
  { id: 70, name: 'Donau-Ries / Noerdlinger Ries', center: { lat: 48.85, lon: 10.50 }, elevation: 430, referenceStation: 'Noerdlingen', stationCoords: { lat: 48.85, lon: 10.49 }, terrainType: 'hills' },

  // --- 71-80: Sued-Deutschland (Bayern, Alpenvorland) ---
  { id: 71, name: 'Oberbayern-Nord / Ingolstadt', center: { lat: 48.75, lon: 11.45 }, elevation: 380, referenceStation: 'Ingolstadt', stationCoords: { lat: 48.76, lon: 11.42 }, terrainType: 'flatland' },
  { id: 72, name: 'Niederbayern / Landshut', center: { lat: 48.55, lon: 12.15 }, elevation: 400, referenceStation: 'Landshut',        stationCoords: { lat: 48.53, lon: 12.15 }, terrainType: 'flatland' },
  { id: 73, name: 'Muenchen / Oberbayern',   center: { lat: 48.15, lon: 11.60 }, elevation: 520, referenceStation: 'Muenchen',        stationCoords: { lat: 48.35, lon: 11.78 }, terrainType: 'flatland' },
  { id: 74, name: 'Augsburg / Lechfeld',     center: { lat: 48.35, lon: 10.90 }, elevation: 490, referenceStation: 'Augsburg',        stationCoords: { lat: 48.43, lon: 10.94 }, terrainType: 'flatland' },
  { id: 75, name: 'Bodensee-Ost / Allgaeu-Vorland', center: { lat: 47.70, lon: 9.80 }, elevation: 500, referenceStation: 'Friedrichshafen', stationCoords: { lat: 47.67, lon: 9.51 }, terrainType: 'hills' },
  { id: 76, name: 'Chiemgau / Inn-Tal',      center: { lat: 47.85, lon: 12.30 }, elevation: 500, referenceStation: 'Rosenheim',       stationCoords: { lat: 47.86, lon: 12.10 }, terrainType: 'hills' },
  { id: 77, name: 'Berchtesgadener Land',    center: { lat: 47.65, lon: 12.95 }, elevation: 600, referenceStation: 'Berchtesgaden',   stationCoords: { lat: 47.63, lon: 13.00 }, terrainType: 'alps' },
  { id: 78, name: 'Passau / Unterer Inn',    center: { lat: 48.55, lon: 13.45 }, elevation: 320, referenceStation: 'Passau',          stationCoords: { lat: 48.57, lon: 13.47 }, terrainType: 'hills' },
  { id: 79, name: 'Schwaben-Sued / Memmingen', center: { lat: 47.95, lon: 10.20 }, elevation: 600, referenceStation: 'Memmingen',     stationCoords: { lat: 47.99, lon: 10.24 }, terrainType: 'hills' },
  { id: 80, name: 'Werdenfelser Land / Garmisch', center: { lat: 47.50, lon: 11.10 }, elevation: 700, referenceStation: 'Garmisch-Partenkirchen', stationCoords: { lat: 47.48, lon: 11.06 }, terrainType: 'alps' },

  // --- 81-83: Alpen ---
  { id: 81, name: 'Wetterstein / Karwendel', center: { lat: 47.40, lon: 11.40 }, elevation: 900, referenceStation: 'Zugspitze',       stationCoords: { lat: 47.42, lon: 10.98 }, terrainType: 'alps' },
  { id: 82, name: 'Bayerische Voralpen / Tegernsee', center: { lat: 47.65, lon: 11.75 }, elevation: 750, referenceStation: 'Wendelstein', stationCoords: { lat: 47.70, lon: 12.01 }, terrainType: 'alps' },
  { id: 83, name: 'Allgaeuer Alpen',         center: { lat: 47.42, lon: 10.28 }, elevation: 800, referenceStation: 'Oberstdorf',      stationCoords: { lat: 47.40, lon: 10.28 }, terrainType: 'alps' },
  { id: 84, name: 'Chiemgauer Alpen',       center: { lat: 47.60, lon: 12.80 }, elevation: 700, referenceStation: 'Traunstein',     stationCoords: { lat: 47.87, lon: 12.64 }, terrainType: 'alps' },
]

// ═══════════════════════════════════════════
// 20 FGL-Regionen (Gruppierung der 83 GAFOR-Gebiete)
// ═══════════════════════════════════════════

export const FGL_REGIONS: FglRegion[] = [
  // 2x Kueste
  {
    id: 'coast-north',
    name: 'Nordseekueste',
    gaforRegions: [1, 5, 6],
    terrainType: 'coast',
    center: { lat: 54.10, lon: 8.50 },
  },
  {
    id: 'coast-east',
    name: 'Ostseekueste',
    gaforRegions: [4, 12, 13],
    terrainType: 'coast',
    center: { lat: 54.10, lon: 12.00 },
  },

  // 4x Norddeutsches Flachland
  {
    id: 'flat-northwest',
    name: 'Nordwest-Flachland',
    gaforRegions: [2, 3, 9, 10],
    terrainType: 'flatland',
    center: { lat: 52.90, lon: 8.20 },
  },
  {
    id: 'flat-north',
    name: 'Norddeutsche Tiefebene',
    gaforRegions: [7, 8, 29, 30],
    terrainType: 'flatland',
    center: { lat: 52.80, lon: 10.00 },
  },
  {
    id: 'flat-northeast',
    name: 'Nordost-Flachland',
    gaforRegions: [11, 14, 15, 16, 40],
    terrainType: 'flatland',
    center: { lat: 52.80, lon: 12.00 },
  },
  {
    id: 'flat-east',
    name: 'Brandenburg / Lausitz',
    gaforRegions: [17, 18, 19, 20],
    terrainType: 'flatland',
    center: { lat: 52.10, lon: 13.40 },
  },

  // 4x Mittelgebirge West
  {
    id: 'hills-eifel',
    name: 'Eifel / Hunsrueck / Mosel',
    gaforRegions: [46, 47, 50, 58],
    terrainType: 'hills',
    center: { lat: 50.30, lon: 6.90 },
  },
  {
    id: 'hills-sauerland',
    name: 'Sauerland / Siegerland',
    gaforRegions: [21, 22, 42, 43, 44],
    terrainType: 'hills',
    center: { lat: 51.30, lon: 7.80 },
  },
  {
    id: 'hills-taunus',
    name: 'Taunus / Rhein-Main / Pfalz',
    gaforRegions: [41, 45, 48, 49, 57, 59],
    terrainType: 'hills',
    center: { lat: 50.20, lon: 7.80 },
  },
  {
    id: 'hills-hessen',
    name: 'Nordhessen / Weserbergland',
    gaforRegions: [23, 24, 51, 52, 53],
    terrainType: 'hills',
    center: { lat: 51.20, lon: 9.60 },
  },

  // 2x Mittelgebirge Mitte
  {
    id: 'hills-harz',
    name: 'Harz / Thueringen',
    gaforRegions: [25, 26, 27, 28],
    terrainType: 'hills',
    center: { lat: 51.20, lon: 10.90 },
  },
  {
    id: 'hills-saxony',
    name: 'Sachsen / Erzgebirge',
    gaforRegions: [31, 32, 33, 34, 35, 36, 37, 38, 39],
    terrainType: 'hills',
    center: { lat: 50.80, lon: 13.00 },
  },

  // 2x Oberrhein / Schwarzwald
  {
    id: 'rhine-north',
    name: 'Oberrhein / Odenwald',
    gaforRegions: [60, 61],
    terrainType: 'hills',
    center: { lat: 49.30, lon: 8.60 },
  },
  {
    id: 'blackforest',
    name: 'Schwarzwald',
    gaforRegions: [62, 63, 64, 65],
    terrainType: 'mountains',
    center: { lat: 48.20, lon: 8.10 },
  },

  // 2x Schwäbische Alb / Franken
  {
    id: 'swabian-alb',
    name: 'Schwäbische Alb / Stuttgart',
    gaforRegions: [66, 67, 68, 69],
    terrainType: 'mountains',
    center: { lat: 48.55, lon: 9.50 },
  },
  {
    id: 'franconia',
    name: 'Franken / Oberpfalz',
    gaforRegions: [54, 55, 70],
    terrainType: 'hills',
    center: { lat: 49.30, lon: 11.20 },
  },

  // 2x Alpenvorland / Alpen
  {
    id: 'bavaria-north',
    name: 'Oberbayern / Niederbayern',
    gaforRegions: [56, 71, 72, 73, 74, 78],
    terrainType: 'flatland',
    center: { lat: 48.50, lon: 11.80 },
  },
  {
    id: 'pre-alps',
    name: 'Alpenvorland',
    gaforRegions: [75, 76, 79],
    terrainType: 'hills',
    center: { lat: 47.80, lon: 10.80 },
  },
  {
    id: 'alps-west',
    name: 'Allgaeuer Alpen',
    gaforRegions: [80, 83],
    terrainType: 'alps',
    center: { lat: 47.45, lon: 10.70 },
  },
  {
    id: 'alps-east',
    name: 'Bayerische Alpen',
    gaforRegions: [77, 81, 82],
    terrainType: 'alps',
    center: { lat: 47.55, lon: 11.70 },
  },
]

// ═══════════════════════════════════════════
// Haversine-Distanz + Naechste-Region-Suche
// ═══════════════════════════════════════════

/**
 * Berechnet die Haversine-Distanz in Kilometern zwischen zwei Koordinaten
 */
function haversineDistanceKm(
  lat1: number, lon1: number,
  lat2: number, lon2: number,
): number {
  const R = 6371 // Erdradius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Findet die naechste GAFOR-Region anhand der GPS-Koordinaten
 */
// DWD-Regionen die tatsächlich eine Segelflugvorhersage haben (geprüft 07.04.2026)
export const DWD_VALID_REGION_IDS = new Set([
  10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28,
  31, 32, 33, 34, 35, 36, 37, 38, 39,
  41, 42, 43, 44, 45, 46, 47,
  50, 51, 52, 53, 54, 55, 56, 57, 58,
  61, 62, 63, 64,
  71, 72, 73, 74, 75, 76,
  81, 82, 83, 84,
])

// ═══════════════════════════════════════════
// Polygon-Grenzen fuer 56 DWD-gueltige GAFOR-Regionen
// Vereinfachte Polygone (4-6 Eckpunkte) als [lat, lon] Paare
// Basierend auf offizieller DWD GAFOR-Karte
// ═══════════════════════════════════════════

const GAFOR_POLYGONS: Record<number, [number, number][]> = {
  // --- 10-13: Schleswig-Holstein / Mecklenburg Kueste ---
  10: [[53.40, 8.50], [53.90, 8.50], [54.90, 8.50], [54.90, 10.30], [53.90, 10.30], [53.40, 9.50]], // Bremen-Wesermarsch / Schleswig-Holstein West
  11: [[53.60, 10.30], [54.50, 10.30], [54.70, 11.00], [54.50, 12.20], [53.60, 12.20], [53.60, 10.80]], // Mecklenburg-West
  12: [[53.80, 12.20], [54.70, 12.20], [54.70, 13.00], [54.50, 14.50], [53.50, 14.50], [53.50, 13.00]], // Mecklenburg-Ost / Ruegen
  13: [[53.00, 10.30], [53.60, 10.30], [53.60, 12.20], [53.80, 12.20], [53.50, 13.00], [53.00, 12.20]], // Vorpommern (Sued)

  // --- 14-19: Brandenburg / Mecklenburg Binnenland ---
  14: [[52.80, 13.00], [53.50, 13.00], [53.50, 14.50], [52.80, 14.50]], // Uckermark
  15: [[52.40, 11.50], [53.00, 11.50], [53.00, 12.80], [52.80, 13.00], [52.40, 13.00]], // Prignitz / Ruppin
  16: [[51.80, 11.00], [52.40, 11.00], [52.40, 11.90], [52.10, 12.00], [51.80, 12.00]], // Altmark / Stendal
  17: [[52.00, 12.50], [52.80, 12.50], [52.80, 13.50], [52.00, 13.50]], // Havelland / Potsdam
  18: [[51.40, 13.20], [52.00, 13.20], [52.00, 14.50], [51.40, 14.80]], // Niederlausitz / Spreewald
  19: [[52.00, 13.50], [52.80, 13.50], [52.80, 14.50], [52.00, 14.80]], // Berlin / Oder

  // --- 20-28: Zentrales Norddeutschland + Mittelgebirge ---
  20: [[51.40, 12.00], [52.00, 12.00], [52.00, 13.20], [51.40, 13.20]], // Flaemig / Wittenberg
  21: [[51.60, 7.00], [52.30, 7.00], [52.30, 8.20], [51.60, 8.20]], // Muensterland
  22: [[51.60, 8.20], [52.30, 8.20], [52.30, 9.20], [51.60, 9.20]], // Teutoburger Wald
  23: [[51.60, 9.20], [52.30, 9.20], [52.30, 10.20], [51.60, 10.20]], // Weserbergland
  24: [[51.30, 9.60], [51.90, 9.60], [51.90, 10.50], [51.30, 10.50]], // Harz-West / Solling
  25: [[51.40, 10.50], [51.90, 10.50], [51.90, 11.50], [51.40, 11.50]], // Harz
  26: [[51.00, 11.50], [51.60, 11.50], [51.60, 12.50], [51.00, 12.50]], // Saale-Unstrut / Halle
  27: [[50.60, 10.20], [51.30, 10.20], [51.30, 11.50], [51.00, 11.50], [50.60, 11.20]], // Thueringer Becken
  28: [[50.20, 10.20], [50.60, 10.20], [50.60, 11.20], [50.80, 11.20], [50.80, 11.50], [50.20, 11.50]], // Thueringer Wald

  // --- 31-39: Sachsen / Erzgebirge ---
  31: [[51.00, 12.00], [51.60, 12.00], [51.60, 12.80], [51.00, 12.80]], // Leipzig
  32: [[51.00, 12.80], [51.60, 12.80], [51.60, 13.50], [51.00, 13.50]], // Nordsachsen / Torgau
  33: [[50.80, 13.20], [51.30, 13.20], [51.30, 14.10], [50.80, 14.10]], // Dresden / Elbtal
  34: [[50.40, 12.40], [50.90, 12.40], [50.90, 13.20], [50.40, 13.20]], // Erzgebirge-Nord
  35: [[50.10, 12.40], [50.40, 12.40], [50.40, 13.40], [50.10, 13.40]], // Erzgebirge-Sued
  36: [[50.90, 14.10], [51.30, 14.10], [51.30, 15.10], [50.90, 15.10]], // Oberlausitz
  37: [[50.40, 13.20], [50.90, 13.20], [50.90, 14.50], [50.40, 14.50]], // Saechs. Schweiz
  38: [[50.10, 11.50], [50.60, 11.50], [50.60, 12.40], [50.10, 12.40]], // Vogtland
  39: [[49.80, 11.40], [50.40, 11.40], [50.40, 12.40], [50.10, 12.40], [49.80, 12.00]], // Oberfranken / Fichtelgebirge

  // --- 41-47: Rhein / Ruhr / West ---
  41: [[50.80, 6.00], [51.50, 6.00], [51.50, 7.00], [50.80, 7.00]], // Niederrhein
  42: [[51.10, 7.00], [51.70, 7.00], [51.70, 8.00], [51.10, 8.00]], // Ruhrgebiet
  43: [[50.80, 7.80], [51.40, 7.80], [51.40, 8.80], [50.80, 8.80]], // Sauerland
  44: [[50.40, 7.50], [50.80, 7.50], [51.10, 7.50], [51.10, 8.40], [50.80, 8.80], [50.40, 8.40]], // Siegerland / Westerwald
  45: [[49.80, 8.20], [50.40, 8.20], [50.40, 9.20], [49.80, 9.20]], // Rhein-Main / Wetterau
  46: [[49.90, 6.00], [50.60, 6.00], [50.80, 6.50], [50.80, 7.50], [50.40, 7.50], [49.90, 7.20]], // Eifel
  47: [[49.40, 6.80], [49.90, 6.80], [49.90, 7.80], [50.10, 7.80], [50.10, 8.20], [49.80, 8.20], [49.40, 7.80]], // Mosel / Hunsrueck

  // --- 50-58: Zentral-Sued ---
  50: [[50.40, 5.80], [50.80, 5.80], [50.80, 6.50], [50.60, 7.00], [50.40, 7.00]], // Aachen
  51: [[50.80, 8.80], [51.60, 8.80], [51.60, 9.60], [50.80, 9.60]], // Nordhessen / Kassel
  52: [[50.20, 9.40], [50.80, 9.40], [50.80, 10.20], [50.20, 10.20]], // Rhoen
  53: [[49.50, 9.20], [50.20, 9.20], [50.20, 10.00], [49.50, 10.20]], // Spessart / Unterfranken
  54: [[49.10, 10.50], [49.80, 10.50], [49.80, 11.50], [49.10, 11.50]], // Mittelfranken
  55: [[49.20, 11.50], [49.80, 11.50], [49.80, 12.50], [49.20, 12.50]], // Oberpfalz
  56: [[48.50, 12.50], [49.20, 12.50], [49.20, 13.50], [48.80, 13.80], [48.50, 13.50]], // Bayerischer Wald-Sued
  57: [[49.10, 7.20], [49.80, 7.20], [49.80, 8.20], [49.10, 8.30]], // Pfalz / Nahe
  58: [[49.00, 6.30], [49.50, 6.30], [49.50, 7.50], [49.10, 7.50], [49.00, 7.20]], // Saarland

  // --- 61-64: Suedwest ---
  61: [[48.70, 7.50], [49.20, 7.50], [49.20, 8.60], [48.70, 8.60]], // Oberrhein-Nord / Karlsruhe
  62: [[48.10, 8.00], [48.70, 8.00], [48.70, 8.80], [48.10, 8.80]], // Nordschwarzwald
  63: [[47.50, 7.50], [48.10, 7.50], [48.10, 8.40], [47.50, 8.40]], // Suedschwarzwald
  64: [[47.50, 7.50], [48.10, 7.50], [48.30, 7.90], [48.30, 8.00], [47.90, 8.00], [47.50, 8.00]], // Oberrhein-Sued / Freiburg

  // --- 71-76: Bayern Sued ---
  71: [[48.30, 10.90], [48.90, 10.90], [48.90, 11.80], [48.30, 11.80]], // Oberbayern-Nord
  72: [[48.20, 11.80], [48.80, 11.80], [48.80, 12.80], [48.20, 12.80]], // Niederbayern / Landshut
  73: [[47.80, 11.00], [48.30, 11.00], [48.30, 12.20], [47.80, 12.20]], // Muenchen / Oberbayern
  74: [[47.90, 10.30], [48.50, 10.30], [48.50, 11.00], [47.90, 11.00]], // Augsburg / Lechfeld
  75: [[47.40, 9.30], [47.90, 9.30], [47.90, 10.30], [47.40, 10.30]], // Bodensee-Ost / Allgaeu-Vorland
  76: [[47.50, 12.00], [47.90, 12.00], [47.90, 13.20], [47.80, 13.50], [47.50, 13.50]], // Chiemgau / Inn-Tal

  // --- 81-83: Alpen ---
  81: [[47.10, 10.80], [47.60, 10.80], [47.60, 11.60], [47.10, 11.60]], // Wetterstein / Karwendel
  82: [[47.30, 11.60], [47.70, 11.60], [47.70, 12.50], [47.30, 12.50]], // Bayerische Voralpen
  83: [[47.10, 10.00], [47.60, 10.00], [47.60, 10.80], [47.10, 10.80]], // Allgaeuer Alpen
  84: [[47.30, 12.50], [47.90, 12.50], [47.90, 13.30], [47.30, 13.30]], // Chiemgauer Alpen
}

// Polygone den Regionen zuweisen
for (const region of GAFOR_REGIONS) {
  if (GAFOR_POLYGONS[region.id]) {
    region.polygon = GAFOR_POLYGONS[region.id]
  }
}

// ═══════════════════════════════════════════
// Point-in-Polygon (Ray-Casting-Algorithmus)
// ═══════════════════════════════════════════

/**
 * Prueft ob ein Punkt innerhalb eines Polygons liegt
 * Verwendet den Ray-Casting-Algorithmus
 * @param lat Breitengrad des Punktes
 * @param lon Laengengrad des Punktes
 * @param polygon Array von [lat, lon] Eckpunkten
 */
export function pointInPolygon(lat: number, lon: number, polygon: [number, number][]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const yi = polygon[i][0], xi = polygon[i][1]
    const yj = polygon[j][0], xj = polygon[j][1]
    if ((yi > lat) !== (yj > lat) && lon < (xj - xi) * (lat - yi) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export function findNearestRegion(lat: number, lon: number): GaforRegion {
  // 1. Polygon-basierte Suche (praeziser)
  for (const region of GAFOR_REGIONS) {
    if (!DWD_VALID_REGION_IDS.has(region.id)) continue
    if (region.polygon && pointInPolygon(lat, lon, region.polygon)) {
      return region
    }
  }

  // 2. Fallback: Naechster Mittelpunkt (Haversine) fuer Randgebiete / ausserhalb DE
  let nearest = GAFOR_REGIONS[0]
  let minDist = Infinity

  for (const region of GAFOR_REGIONS) {
    if (!DWD_VALID_REGION_IDS.has(region.id)) continue
    const dist = haversineDistanceKm(lat, lon, region.center.lat, region.center.lon)
    if (dist < minDist) {
      minDist = dist
      nearest = region
    }
  }

  return nearest
}

/**
 * Findet die naechste FGL-Region (20er Gruppierung) anhand der GPS-Koordinaten
 */
export function findNearestFglRegion(lat: number, lon: number): FglRegion {
  let nearest = FGL_REGIONS[0]
  let minDist = Infinity

  for (const region of FGL_REGIONS) {
    const dist = haversineDistanceKm(lat, lon, region.center.lat, region.center.lon)
    if (dist < minDist) {
      minDist = dist
      nearest = region
    }
  }

  return nearest
}

/**
 * Prueft ob eine GPS-Position in Deutschland liegt (grobe Bounding-Box)
 */
export function isInGermany(lat: number, lon: number): boolean {
  return lat >= 47.2 && lat <= 55.1 && lon >= 5.8 && lon <= 15.1
}

/**
 * Gibt die GAFOR-Region anhand der ID zurueck
 */
export function getRegionById(id: number): GaforRegion | undefined {
  return GAFOR_REGIONS.find(r => r.id === id)
}

/**
 * Gibt die FGL-Region zurueck, die eine bestimmte GAFOR-Region enthaelt
 */
export function getFglRegionForGafor(gaforId: number): FglRegion | undefined {
  return FGL_REGIONS.find(r => r.gaforRegions.includes(gaforId))
}
